import React, { useEffect, useState, useContext } from 'react';
import {
  UserCheck, ClipboardList, BookOpen, Star, Trophy,
  Users, TrendingUp, Calendar, Clock, CheckCircle2,
  XCircle, AlertCircle, GraduationCap, BarChart2,
} from 'lucide-react';
import { BarChartComponent, AreaChartComponent } from '../components/Charts/ChartComponents';
import { dashboardAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { YearContext } from '../components/Layout/Layout';
import { PageLoader } from '../components/Common/LoadingSpinner';

const MONTH_NAMES = ['','Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

// Maps performance score → banner gradient + subtitle text colour
// Matches the Teacher Observation Rating Scale (5 = Excelling … 1 = Developing)
function scoreTheme(score) {
  const s = parseFloat(score) || 0;
  if (s <= 0) return { grad: 'from-primary-600 to-primary-800', sub: 'text-primary-200'  }; // no data
  if (s > 4)  return { grad: 'from-emerald-500 to-teal-700',    sub: 'text-emerald-100'  }; // Excelling  4.1–5.0
  if (s > 3)  return { grad: 'from-blue-500 to-blue-700',       sub: 'text-blue-100'     }; // Proficient 3.1–4.0
  if (s > 2)  return { grad: 'from-violet-500 to-purple-700',   sub: 'text-violet-100'   }; // Achieving  2.1–3.0
  if (s > 1)  return { grad: 'from-amber-400 to-orange-600',    sub: 'text-amber-100'    }; // Meeting    1.1–2.0
  return       { grad: 'from-red-500 to-rose-700',              sub: 'text-red-100'      }; // Developing 0–1.0
}

const STATUS_CFG = {
  present: { label: 'Present', color: 'bg-emerald-500', text: 'text-emerald-700', bg: 'bg-emerald-50', icon: CheckCircle2 },
  late:    { label: 'Late',    color: 'bg-amber-400',   text: 'text-amber-700',   bg: 'bg-amber-50',   icon: Clock },
  absent:  { label: 'Absent',  color: 'bg-red-500',     text: 'text-red-700',     bg: 'bg-red-50',     icon: XCircle },
  holiday: { label: 'Holiday', color: 'bg-slate-300',   text: 'text-slate-600',   bg: 'bg-slate-50',   icon: Calendar },
};

function MiniStatCard({ icon: Icon, label, value, sub, color }) {
  const colors = {
    green:  { ring: 'ring-emerald-200', bg: 'bg-emerald-50',  icon: 'text-emerald-600', val: 'text-emerald-700' },
    blue:   { ring: 'ring-blue-200',    bg: 'bg-blue-50',     icon: 'text-blue-600',    val: 'text-blue-700'    },
    purple: { ring: 'ring-violet-200',  bg: 'bg-violet-50',   icon: 'text-violet-600',  val: 'text-violet-700'  },
    amber:  { ring: 'ring-amber-200',   bg: 'bg-amber-50',    icon: 'text-amber-600',   val: 'text-amber-700'   },
    teal:   { ring: 'ring-teal-200',    bg: 'bg-teal-50',     icon: 'text-teal-600',    val: 'text-teal-700'    },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className={`bg-white rounded-2xl p-5 ring-1 ${c.ring} flex flex-col gap-2`}>
      <div className={`w-10 h-10 rounded-xl ${c.bg} flex items-center justify-center`}>
        <Icon size={20} className={c.icon} />
      </div>
      <p className={`text-2xl font-display font-bold ${c.val}`}>{value ?? '—'}</p>
      <div>
        <p className="text-sm font-medium text-slate-700">{label}</p>
        {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
      </div>
    </div>
  );
}

function AttendanceDot({ record }) {
  const cfg = STATUS_CFG[record.status] || STATUS_CFG.holiday;
  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${cfg.bg}`}>
      <span className={`w-2 h-2 rounded-full ${cfg.color} flex-shrink-0`} />
      <span className="text-xs text-slate-600 font-medium w-16 flex-shrink-0">
        {record.date ? new Date(record.date).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '—'}
      </span>
      <span className={`text-xs font-semibold ${cfg.text}`}>{cfg.label}</span>
      {record.in_time && (
        <span className="text-xs text-slate-400 ml-auto">{record.in_time.slice(0,5)}</span>
      )}
    </div>
  );
}

export default function TeachersDashboard() {
  const { yearId, refreshKey } = useContext(YearContext);
  const { user } = useAuth();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    dashboardAPI.getTeacherOverview({ academicYearId: yearId })
      .then(r => setData(r.data))
      .catch(() => setData({ found: false }))
      .finally(() => setLoading(false));
  }, [yearId, refreshKey]);

  if (loading) return <PageLoader />;

  if (!data?.found) {
    return (
      <div className="flex flex-col items-center justify-center py-24 text-center">
        <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
          <GraduationCap size={28} className="text-slate-400" />
        </div>
        <h3 className="text-lg font-semibold text-slate-700 mb-1">No employee record linked</h3>
        <p className="text-sm text-slate-400 max-w-xs">
          Your account is not linked to an employee profile. Contact the administrator to set this up.
        </p>
      </div>
    );
  }

  const { employee, attendance, lessonPlan, homework, performance, classes, perfTrend, recentAttendance } = data;

  // Unique classes (grade+section combos)
  const uniqueClasses = [];
  const seen = new Set();
  for (const c of classes) {
    const key = `${c.grade}-${c.section}`;
    if (!seen.has(key)) { seen.add(key); uniqueClasses.push({ grade: c.grade, section: c.section }); }
  }
  // Subjects per class
  const subjectsByClass = {};
  for (const c of classes) {
    const key = `${c.grade}-${c.section}`;
    if (!subjectsByClass[key]) subjectsByClass[key] = [];
    if (c.subject && !subjectsByClass[key].includes(c.subject)) subjectsByClass[key].push(c.subject);
  }

  const perf = performance;
  const theme = scoreTheme(perf?.totalScore);

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Welcome header — colour driven by performance score */}
      <div className={`bg-gradient-to-r ${theme.grad} rounded-2xl p-6 text-white shadow-lg`}>
        <div className="flex items-start justify-between flex-wrap gap-4">
          <div>
            <p className={`${theme.sub} text-sm font-medium mb-1`}>Welcome back</p>
            <h2 className="text-2xl font-display font-bold">{employee.name}</h2>
            {employee.employee_code && (
              <p className={`${theme.sub} text-sm mt-1`}>{employee.employee_code}</p>
            )}
          </div>
          {perf && (
            <div className="flex items-center gap-3">
              <div className="text-center bg-white/15 rounded-xl px-5 py-3">
                <p className="text-3xl font-display font-bold">{perf.totalScore ?? '—'}</p>
                <p className={`${theme.sub} text-xs mt-0.5`}>Performance Score</p>
              </div>
              {perf.rank && (
                <div className="text-center bg-white/15 rounded-xl px-5 py-3">
                  <p className="text-3xl font-display font-bold">#{perf.rank}</p>
                  <p className={`${theme.sub} text-xs mt-0.5`}>Rank</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Key stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <MiniStatCard
          icon={UserCheck} label="My Attendance"
          value={`${attendance.pct}%`}
          sub={`${attendance.presentDays}P · ${attendance.absentDays}A · ${attendance.lateDays}L`}
          color="green"
        />
        <MiniStatCard
          icon={ClipboardList} label="Lesson Plans"
          value={`${lessonPlan.avgPct}%`}
          sub={`${lessonPlan.fullyDone} of ${lessonPlan.total} completed`}
          color="purple"
        />
        <MiniStatCard
          icon={BookOpen} label="Homework Completion"
          value={`${homework.completionPct}%`}
          sub={`${homework.total} assignments`}
          color="amber"
        />
        <MiniStatCard
          icon={perf ? Trophy : Star} label="Performance Score"
          value={perf ? `${perf.totalScore}/100` : 'No data'}
          sub={perf ? `${MONTH_NAMES[perf.month]} ${perf.year} · Rank #${perf.rank ?? '—'}` : 'Run performance compute'}
          color="teal"
        />
      </div>

      {/* Score breakdown + Classes */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Score breakdown */}
        {perf ? (
          <div className="bg-white rounded-2xl p-5 ring-1 ring-slate-200">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
              <BarChart2 size={16} className="text-primary-500" /> Score Breakdown
              <span className="ml-auto text-xs text-slate-400 font-normal">
                {MONTH_NAMES[perf.month]} {perf.year}
              </span>
            </h3>
            <div className="space-y-3">
              {[
                { label: 'Attendance',   score: perf.attendanceScore,  max: 25, color: 'bg-emerald-500' },
                { label: 'Lesson Plans', score: perf.lessonPlanScore,  max: 25, color: 'bg-violet-500'  },
                { label: 'Homework',     score: perf.homeworkScore,    max: 25, color: 'bg-amber-500'   },
                { label: 'Observation',  score: perf.observationScore, max: 25, color: 'bg-sky-500'     },
              ].map(({ label, score, max, color }) => (
                <div key={label}>
                  <div className="flex justify-between text-xs text-slate-600 mb-1">
                    <span>{label}</span>
                    <span className="font-semibold">{score ?? 0} / {max}</span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div
                      className={`h-full ${color} rounded-full transition-all`}
                      style={{ width: `${Math.min(((score || 0) / max) * 100, 100)}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-2xl p-5 ring-1 ring-slate-200 flex flex-col items-center justify-center text-center">
            <Trophy size={32} className="text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No performance scores yet</p>
            <p className="text-xs text-slate-400 mt-1">Performance scores appear after the first monthly compute</p>
          </div>
        )}

        {/* Assigned Classes */}
        <div className="bg-white rounded-2xl p-5 ring-1 ring-slate-200">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Users size={16} className="text-primary-500" /> My Assigned Classes
            <span className="ml-auto text-xs bg-primary-50 text-primary-700 font-semibold px-2 py-0.5 rounded-full">
              {uniqueClasses.length}
            </span>
          </h3>
          {uniqueClasses.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <GraduationCap size={24} className="text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">No class assignments found</p>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2 max-h-52 overflow-y-auto">
              {uniqueClasses.map(cls => {
                const key = `${cls.grade}-${cls.section}`;
                const subjects = subjectsByClass[key] || [];
                return (
                  <div key={key} className="bg-slate-50 rounded-xl p-3">
                    <p className="font-bold text-slate-800 text-sm">{cls.grade} – {cls.section}</p>
                    {subjects.length > 0 && (
                      <p className="text-xs text-slate-500 mt-1 truncate">{subjects.join(', ')}</p>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Performance trend + Recent attendance */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {perfTrend.length > 0 ? (
          <AreaChartComponent
            title="My Performance Trend"
            subtitle="Monthly total score across the academic year"
            data={perfTrend}
            xKey="month"
            areas={[{ key: 'total', label: 'Total Score', color: '#6d28d9' }]}
          />
        ) : (
          <div className="bg-white rounded-2xl p-5 ring-1 ring-slate-200 flex flex-col items-center justify-center text-center min-h-[200px]">
            <TrendingUp size={28} className="text-slate-300 mb-2" />
            <p className="text-sm text-slate-500">No performance trend data yet</p>
          </div>
        )}

        {/* Recent Attendance */}
        <div className="bg-white rounded-2xl p-5 ring-1 ring-slate-200">
          <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Calendar size={16} className="text-primary-500" /> Recent Attendance
          </h3>
          {recentAttendance.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <Calendar size={24} className="text-slate-300 mb-2" />
              <p className="text-sm text-slate-400">No attendance records found</p>
            </div>
          ) : (
            <div className="space-y-1.5 max-h-52 overflow-y-auto">
              {recentAttendance.map((r, i) => <AttendanceDot key={i} record={r} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
